<template>
  <div data-test-id="product-reviews">
    <define-write-review-cta>
      <div class="text-center" data-test-id="product-reviews-add">
        <vf-button class="<md:w-full" :variant @click="openWriteReviewModal">
          {{ reviewsCount ? $t.writeAReview : $t.writeFirstReview }}
        </vf-button>
      </div>
    </define-write-review-cta>

    <define-below-review-snapshot>
      <write-review-cta v-if="reviewsConfig.showWriteReviewCta" />
      <product-reviews-rollup-carousel
        v-if="$feature.showReviewsRollupCarouselOnPDP && reviewUgcMedia && reviewUgcMedia.length >= 3"
        :product-id="productId"
        :review-ugc-media="reviewUgcMedia"
      />
      <template v-if="showFilters">
        <product-reviews-filters
          v-if="reviewsConfig.showFilterPills"
          :current-reviews-count="currentReviewsCount"
          :filters
          @filter-by="filterBy"
          @open-filters-panel="DialogFilters.keep().open()"
          @update:filters="filters = $event"
        />
        <section data-test-id="product-reviews-search">
          <div :class="brandClasses.reviewsSearchSortContainer" data-test-id="total-reviews-by-customer">
            <product-reviews-search
              v-model="searchTerm"
              :icon-size="reviewsConfig.iconSize"
              :reviews-count="reviewsCount"
              @search="handleSearchSubmit"
            />
            <product-reviews-sort v-model="reviewsQuery.sort" :sort-options="sortOptions" />
            <filter-display
              :currency
              :filters
              :heading="$t.filtersApplied"
              @clear="clearFilters"
              @remove="removeFilter"
            />
          </div>
        </section>
      </template>
      <vf-notification v-if="error" :dismissible="false" type="error">
        {{ errorMsg }}
      </vf-notification>
      <product-reviews-list
        v-else
        v-bind="{
          currentPageNumber,
          currentReviewsCount,
          currentReviewsDisplayed,
          pagesCount,
          productId,
          reviews,
          reviewsCount,
          searchTerm,
          variant,
        }"
        :aria-label="`${$t.reviews} - ${currentReviewsDisplayed}`"
        data-scroll-el="reviews-list"
        :page-size="reviewsQuery['paging.size']"
        :reviews-pending="pending"
        @back-to-top="scrollToReviews"
        @next="showNext"
        @previous="showPrevious"
      />
    </define-below-review-snapshot>

    <div :class="brandClasses.reviewsWrapper">
      <template v-if="reviewsCount">
        <h3 v-if="showHeading" :class="brandClasses.reviewsHeading">
          {{ $t.reviewSnapshot }}
        </h3>
        <section
          :class="[brandClasses.reviewsSnapshotWrapper, fitRatings.length ? 'lg:cols-3' : 'lg:cols-2']"
          data-test-id="review-snapshot-section"
        >
          <product-reviews-snapshot
            :average-rating="rollup.averageRating"
            :ratio
            :recommendation-rate-threshold="recommendationRateThreshold"
            :reviews-count="reviewsCount"
            @scroll-to-reviews="scrollToReviews"
            @write-review="openWriteReviewModal"
          />
          <product-reviews-rating-breakdown
            v-if="reviewsConfig.showRatingBreakdown"
            :rating-filter="ratingFilter"
            :rating-histogram="ratingHistogram"
            :reviews-count="reviewsCount"
            @filter-by-rating="handleFilterByRating"
          />
          <product-reviews-fit-rating
            v-if="fitRatings.length && reviewsConfig.showRatingBreakdown"
            :fit-ratings="fitRatings"
          />
          <product-reviews-pros-and-cons v-if="prosAndCons" :pros-and-cons="prosAndCons" />
          <product-reviews-faceoff
            v-if="$feature.showReviewFaceoffOnPDP && (mostLikedPositive || mostLikedNegative)"
            class="col-span-full"
            :most-liked-comment-text-threshold="reviewsConfig.mostLikedCommentTextThreshold"
            :most-liked-negative="mostLikedNegative"
            :most-liked-positive="mostLikedPositive"
            :rating-histogram="ratingHistogram"
          />
        </section>
        <vf-accordion v-if="$feature.showReviewsAsDropdownOnPDP" v-model="reviewsExpanded" variant="aligned">
          <template #toggle="{ expanded }">
            <span class="mx-4 flex items-center py-4 between" data-test-id="vf-accordion-toggle">
              <span class="subtitle-4">
                {{ $t.reviews }}
                <span class="fw-normal">
                  ({{ reviewsCount }})
                </span>
              </span>
              <span class="flex items-center gap-1">
                <vf-rating v-if="rollup.averageRating" :value="rollup.averageRating" />
                <vf-icon :dir="expanded ? 'up' : 'down'" name="chevron" size="lg" />
              </span>
            </span>
          </template>
          <div class="space-y-4">
            <below-review-snapshot />
          </div>
        </vf-accordion>
        <below-review-snapshot v-else />
      </template>
      <write-review-cta v-else />
    </div>
    <dialog-review-write hide-backdrop />
    <dialog-review-ugc class-content="md:w-152 lg:w-164" size="md" />
    <dialog-filters
      :currency
      :filters="mobileFilters"
      :heading="$t.filterReviews"
      position="left"
      :total="currentReviewsCount"
      @clear="clearFilters"
      @remove="removeFilter"
      @update="updateFilters"
    />
  </div>
</template>

<script lang="ts" setup>
import type { ProductReviewsRollup, ProductReviewsRollupProperty } from '#root/api/clients/product/data-contracts'
import type { MappedProsAndCons, ProductReviewsData, ProductReviewsParams } from '#types/components/product/reviews'
import type { FiltersMap, HistogramRatingFilter, SearchFilter } from '#types/filters'
import { ApiErrorCode } from '#root/enums/api'

const { showHeading = true, data, error, productId } = defineProps<{
  currency?: string
  data?: ProductReviewsData | null
  error: any
  pending: boolean
  productId: string
  showHeading?: boolean
}>()

const enum SpecialFilterCodes {
  Rating = 'rating',
  Search = 'search',
}

const reviewsQuery = defineModel<ProductReviewsParams>('modelValue', { required: true })

const {
  brandClasses,
  reviews: { variant, recommendationRateThreshold, dialogOptions, ...reviewsConfig },
} = useAppConfig().pages.pdp
const { DialogReviewUgc } = useDialogsStore()
const { $gtm, $pluralize, $t, $feature } = useNuxtApp()
const route = useRoute()

const DialogFilters = createDialog('filters', dialogOptions.filters)
const DialogReviewWrite = createDialog('review-write', dialogOptions.reviewWrite)

const [DefineBelowReviewSnapshot, BelowReviewSnapshot] = createReusableTemplate()
const [DefineWriteReviewCta, WriteReviewCta] = createReusableTemplate()

const rollup = computed(() => data?.rollup || {} as ProductReviewsRollup)
const reviewsCount = computed(() => data?.rollup?.reviewCount || 0)
const ratingHistogram = computed(() =>
  rollup.value?.ratingHistogram?.map((reviewCount, i) => ({
    rating: i + 1,
    reviewCount
  })).reverse()
)
const fitRatings = computed(() => rollup.value?.properties?.filter(({ display_type }) => display_type === 'histogram') || [])
const ratio = computed(() => Math.round((rollup.value?.recommendedRatio || 0) * 100))
const errorId = computed(() => error?.cause?.errorId)
const errorMsg = computed(() =>
  errorId.value === ApiErrorCode.REVIEWS_MAX_LENGTH
    ? $t.reviewsTooManyFiltersError
    : $t.reviewsError
)
const showFilters = computed(
  () => reviewsConfig.showFilters && (reviewsCount.value || errorId.value === ApiErrorCode.REVIEWS_MAX_LENGTH)
)

// Reviews State
const reviews = computed(() => data?.reviews || [])
const currentReviewsCount = computed(() => data?.paging.totalResults || 0)
const pagesCount = computed(() => data?.paging.pagesTotal || 1)
const currentPageNumber = computed(() => data?.paging.currentPageNumber || 1)

// Pros & Cons
const prosAndCons = computed<MappedProsAndCons[] | undefined>(() => {
  if (!reviewsConfig.showProsAndCons) return
  const prosCons: ProductReviewsRollupProperty[]
    = rollup.value?.properties?.filter(({ key }) => key === 'pros' || key === 'cons') || []
  if (reviewsConfig.prosAndConsMaxNumber && prosCons.length) {
    prosCons[0].values.splice(reviewsConfig.prosAndConsMaxNumber)
    prosCons[1].values.splice(reviewsConfig.prosAndConsMaxNumber)
  }
  return prosCons.map(({ key, values }) => ({
    title: $t[key],
    values
  })).reverse()
})

// UGC Media
const reviewUgcMedia = computed(() =>
  rollup.value?.media?.slice(0, 7).map((item) => ({
    reviewId: Number(item.id),
    details: {
      // @ts-expect-error incorrect contract: "nickname" is missing
      nickname: item.nickname,
      headline: '',
      locale: '',
      location: '',
      createdDate: 0,
      updatedDate: 0,
      comments: '',
      properties: []
    },
    metrics: {
      helpfulVotes: item.helpful_votes,
      notHelpfulVotes: item.not_helpful_votes,
      rating: 0,
      helpfulScore: 0
    },
    media: [item],
    ugcId: 0,
    legacyId: 0,
    badges: {
      isStaffReviewer: false,
      isVerifiedBuyer: false,
      isVerifiedReviewer: false
    }
  }))
)

const fields = ['rating', 'comments', 'headline']
const mostLikedPositive = computed(() =>
  fields.some((field) => rollup.value?.faceoff.positive?.[field]) ? rollup.value?.faceoff.positive : null
)
const mostLikedNegative = computed(() =>
  fields.some((field) => rollup.value?.faceoff.negative?.[field]) ? rollup.value?.faceoff.negative : null
)

// Filters State
const filtersToUse = computed(() =>
  rollup.value?.properties?.filter(({ key, display_type }) =>
    !reviewsConfig.nonFilterKeys.includes(key)
    && display_type !== 'histogram'
  ) || []
)

const initializeFilters = () => {
  const tagFilters = Object.fromEntries(
    filtersToUse.value.map(({ key, name, values }) => {
      values.splice(reviewsConfig.filterOptionsMaxNumber)
      return [key, {
        code: key,
        facetId: key,
        label: name,
        options: values.map(({ count, label }) => ({
          label,
          id: label,
          items: count,
          checked: false,
          disabled: false
        })),
        type: 'Checkbox',
        selected: []
      }]
    })
  ) as FiltersMap
  const ratingFilters = {
    code: SpecialFilterCodes.Rating,
    facetId: SpecialFilterCodes.Rating,
    label: $t.sorts.rating,
    options: ratingHistogram.value?.map(({ rating, reviewCount }) => ({
      label: `${rating} ${$pluralize($t.stars, rating)}`,
      id: `${rating} ${$pluralize($t.stars, rating)}`,
      items: reviewCount,
      checked: false,
      disabled: false
    })),
    type: 'HistogramRating',
    selected: []
  } as HistogramRatingFilter
  const searchFilter = {
    code: SpecialFilterCodes.Search,
    facetId: SpecialFilterCodes.Search,
    label: $t.searchLabel,
    type: 'Search',
    selected: []
  } as SearchFilter
  return { ...tagFilters, rating: ratingFilters, search: searchFilter }
}

const sortOptions = [
  { value: 'Newest', label: $t.reviewSortOptionNewest },
  { value: 'MostHelpful', label: $t.reviewSortOptionMostHelpful },
  { value: 'HighestRating', label: $t.reviewSortOptionHighestRating },
  { value: 'LowestRating', label: $t.reviewSortOptionLowestRating },
  { value: 'Oldest', label: $t.reviewSortOptionOldest },
  { value: 'MediaSort', label: $t.reviewSortOptionMediaSort }
]

const filters = ref<FiltersMap>(initializeFilters())
const ratingFilter = ref(0)
const reviewsExpanded = ref(false)
const searchTerm = ref()

// Reviews Display
const currentReviewsDisplayed = computed(() => {
  const start = ((currentPageNumber.value - 1) * reviewsQuery.value['paging.size']) + 1
  const end = Math.min(currentReviewsCount.value, currentPageNumber.value * reviewsQuery.value['paging.size'])
  return currentReviewsCount.value
    ? replaceAll($t.displayingReviews, { start, end })
    : $t.noResults
})

const scrollToReviews = () => {
  if ($feature.showReviewsAsDropdownOnPDP) reviewsExpanded.value = true
  scrollToElement('reviews-list')
  $gtm.push('pdpPage.onViewReviews')
}

const filterBy = () => {
  const filtersQuery: string[] = []
  let searchQuery
  Object.values(filters.value)
    .filter(({ selected }) => selected?.length)
    .forEach(({ code, selected }) => {
      selected?.forEach((item) => {
        if (code === SpecialFilterCodes.Search) searchQuery = searchTerm.value
        else if (code === SpecialFilterCodes.Rating) filtersQuery.push(`${code}:${item.split(' ')[0]}`)
        else filtersQuery.push(`${code}:${item}`)
      })
    })
  reviewsQuery.value.filters = filtersQuery.length ? filtersQuery.join(',') : undefined
  reviewsQuery.value['paging.from'] = 0
  reviewsQuery.value.q = searchQuery
}

const handleFilterByRating = (rating: number) => {
  ratingFilter.value = rating !== ratingFilter.value ? rating : 0
  if (ratingFilter.value)
    filters.value.rating.selected = [`${rating} ${$pluralize($t.stars, rating)}`]
  else filters.value.rating.selected = []

  filterBy()
  scrollToReviews()
}

const handleSearchSubmit = () => {
  filters.value.search.selected = [searchTerm.value]
  filterBy()
}

const removeFilter = ({ code, id }: { code: string, id: string }) => {
  filters.value[code].selected = filters.value[code].selected?.filter((item) => item !== id)
  if (code === SpecialFilterCodes.Rating) ratingFilter.value = 0
  if (code === SpecialFilterCodes.Search) {
    searchTerm.value = undefined
    filters.value.search.selected = []
  }

  filterBy()
}

const clearFilters = () => {
  Object.keys(filters.value).forEach((code) => filters.value[code].selected = [])
  ratingFilter.value = 0
  filters.value.search.selected = []
  searchTerm.value = undefined
  filterBy()
}

const mobileFilters = computed<FiltersMap>(() => Object.fromEntries(
  Object.entries(filters.value).filter(([f]) => f !== SpecialFilterCodes.Rating && f !== SpecialFilterCodes.Search)
))

const updateFilters = ({ code, selected }) => {
  filters.value[code].selected = selected
  filterBy()
}

const showPrevious = () => {
  reviewsQuery.value['paging.from'] = (currentPageNumber.value - 1) * reviewsQuery.value['paging.size'] - reviewsQuery.value['paging.size']
  scrollToReviews()
}

const showNext = () => {
  reviewsQuery.value['paging.from'] = (currentPageNumber.value + 1) * reviewsQuery.value['paging.size'] - reviewsQuery.value['paging.size']
  scrollToReviews()
}

const openWriteReviewModal = () => {
  DialogReviewWrite.keep().open({
    pageId: data?.reviewsPageId || data?.pageId,
    productId,
  })
}

if ($feature.showReviewsAsDropdownOnPDP)
  watchImmediate(() => route.hash, () => reviewsExpanded.value = !!route.hash)

// Cleanup
onBeforeUnmount(() => {
  reviewsQuery.value['paging.from'] = 0
  reviewsQuery.value.sort = 'Newest'
  reviewsQuery.value.filters = undefined
  reviewsQuery.value.q = undefined
})
</script>
